import Mustache from 'mustache';
import { identity, mapValues } from 'lodash';

const keywords = ['break'];

const NEW_LINE = `
`;

export class InvalidTemplateError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidTemplateError';
  }
}

export const validate = (template: string, availableVariables?: string[]) => {
  // do all the opening {{ brackets have a closing }} bracket?
  const matches = template.match(/{{|}}/g);
  if (matches && matches.length % 2 !== 0) {
    throw new InvalidTemplateError(
      `Invalid template. Missing closing bracket. template: ${JSON.stringify({
        template,
      })}`
    );
  }

  // are all the variables available in the availableVariables array?
  const variables = Mustache.parse(template).filter(
    token => token[0] === 'name'
  );

  const variableNames = variables.map(variable => variable[1]);
  const invalidVariables = variableNames.filter(
    variable =>
      !availableVariables?.includes(variable) &&
      !keywords.includes(variable) &&
      !variable.startsWith('flag#') &&
      !variable.startsWith('#') &&
      !variable.startsWith('/') &&
      !variable.startsWith('.')
  );

  if (invalidVariables.length) {
    throw new InvalidTemplateError(
      `Invalid template. Invalid variables. template: ${JSON.stringify({
        template,
        invalidVariables,
      })}`
    );
  }
};

const applyBreaks = (value: string) => value.split('{{break}}');

const applyFlags = (value: string): { [key: string]: string } => {
  let flaggedValue = value;
  if (!value.startsWith('{{flag#')) {
    flaggedValue = `{{flag#main}}${value}`;
  }

  const obj: { [key: string]: string } = {};
  const flagRegex = /\{\{flag#([A-Za-z0-9_-]+)\}\}/g;
  let match;

  while ((match = flagRegex.exec(flaggedValue)) !== null) {
    const flagName = match[1];

    const start = match.index + match[0].length;
    let end = flaggedValue.indexOf('{{flag#', start);

    if (end === -1) {
      end = flaggedValue.length;
    }

    const flagContent = flaggedValue.slice(start, end).trim();

    obj[flagName] = flagContent;
  }

  return obj;
};

export const render = (
  template: string,
  variables: Record<string, string | undefined | null>
) => {
  validate(template, Object.keys(variables));

  const result = mapValues(applyFlags(template), flaggedTemplate =>
    applyBreaks(flaggedTemplate).map(brokenTemplate =>
      Mustache.render(brokenTemplate, variables, undefined, {
        escape: identity,
      })
    )
  );

  const mainArray = Object.values(result).flat();

  return new Proxy(mainArray as any, {
    get: (target, prop) => {
      switch (prop) {
        case 'getFlag':
          return (flagName: string) => {
            if (!result[flagName])
              throw new InvalidTemplateError(
                `Flag ${flagName} not found in the template: ${template}`
              );
            return result[flagName];
          };

        case 'hasFlag':
          return (flagName: string) => !!result[flagName];
        case 'toString':
          return () => mainArray.join(NEW_LINE);
      }

      return Reflect.get(target, prop) as typeof Array & {
        getFlag: (flagName: string) => string[];
        hasFlag: (flagName: string) => boolean;
      };
    },
  }) as typeof mainArray & {
    getFlag: (flagName: string) => string[];
    hasFlag: (flagName: string) => boolean;
  };
};
